import React, { useState } from 'react';
import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { CentsDataGridServer } from 'components/common/dataGrid/dataGridServer';
import TableSearchBar from 'components/common/search/TableSearchBar';
import { DEFAULT_PAGE_LIMIT } from 'utils/constants';
import { getServiceOrders } from 'api/admin/orders/orders';

const COLUMNS = [
  { headerName: 'ID', field: 'id', flex: 1, sortable: false },
  { headerName: 'Order Code', field: 'orderCode', flex: 1, sortable: false },
  { headerName: 'Store Name', field: 'storeName', flex: 1, sortable: false },
  { headerName: 'Customer Name', field: 'customerName', flex: 1, sortable: false },
  { headerName: 'Status', field: 'status', flex: 1, sortable: false },
  { headerName: 'Order Total', field: 'orderTotal', flex: 1, sortable: false },
  { headerName: 'Net Order Total', field: 'netOrderTotal', flex: 1, sortable: false },
  { headerName: 'Placed At', field: 'placedAt', flex: 1.5, sortable: false },
  { headerName: 'Due Date', field: 'dueDate', flex: 1.5, sortable: false },
  { headerName: 'Completed At', field: 'completedAt', flex: 1.5, sortable: false },
  { headerName: 'Payment Status', field: 'paymentStatus', flex: 1, sortable: false },
  { headerName: 'Payment Timing', field: 'paymentTiming', flex: 1, sortable: false },
  { headerName: 'Tip Amount', field: 'tipAmount', flex: 1, sortable: false },
  { headerName: 'Credit Amount', field: 'creditAmount', flex: 1, sortable: false },
  { headerName: 'Promotion Amount', field: 'promotionAmount', flex: 1, sortable: false },
  { headerName: 'Balance Due', field: 'balanceDue', flex: 1, sortable: false },
];

const ServiceOrdersTable = () => {
  const history = useHistory();
  const [searchString, setSearchString] = useState('');
  const [pageNumber, setPageNumber] = useState(0);
  const [rowCount, setRowCount] = useState(0);

  const { data, isLoading } = useQuery(
    ['searchServiceOrders', { searchString, pageNumber }],
    async () => {
      const { data } = await getServiceOrders(pageNumber, searchString);

      /**
       * due to performance issues, our API to fetch service orders now only returns 20 service orders as the total
       * however, we want to make sure we can click to the next page of service orders in datagrid
       *
       * so here, we're tricking datagrid into thinking there are more service orders to fetch by modifying the rowCount
       *
       * if the returned total is 20, that means there are likely more results to fetch and the API returned the max
       * in that case, we take 20, multiply it by the page number + 1 (since page number starts at 0), and add 1,
       * making the total rowCount of 21.
       *
       * if the total is not 20, then we know we've reached the end of the results, and we set the rowCount to the total
       */
      const adjustedPaginationTotal =
        data.total === DEFAULT_PAGE_LIMIT ? (pageNumber + 1) * DEFAULT_PAGE_LIMIT + 1 : data.total;
      setRowCount(adjustedPaginationTotal);

      return {
        serviceOrders: data.serviceOrders,
        serviceOrderCount: data.total,
      };
    },
    {
      onError: (error) => {
        // maintaining existing error handling behavior
        console.log(error);
        Sentry.captureException(error);
      },
    },
  );
  const { serviceOrders } = data ?? {};

  const handleRowClick = ({ id }) => {
    history.push(`/service-orders/${Number(id)}`);
  };

  const handlePageChange = ({ page }) => {
    setPageNumber(page);
  };

  const handleSearch = (searchInput) => {
    if (searchInput.length > 0) {
      if (searchInput !== searchString) {
        setPageNumber(0);
      }
      setSearchString(searchInput);
    } else {
      setPageNumber(0);
      setSearchString('');
    }
  };

  return (
    <div className="page-container">
      <h1 className="service-orders-header">Service Orders</h1>
      <div className="service-orders-search-bar">
        <TableSearchBar
          value={searchString}
          placeholder="Find a service order"
          onSearch={handleSearch}
        />
      </div>
      <CentsDataGridServer
        tableName="SERVICE_ORDERS"
        columns={COLUMNS}
        data={serviceOrders ?? []}
        rowCount={rowCount}
        pageSize={DEFAULT_PAGE_LIMIT}
        page={pageNumber}
        onPageChange={handlePageChange}
        onRowClick={handleRowClick}
        isLoading={isLoading}
      />
    </div>
  );
};

export default ServiceOrdersTable;
